import {
  APPLICATION_STATUS,
  MANAGER_WORKFLOW_STEP_ID,
  MANAGER_WORKFLOW_STEP_STATE,
} from '../../../../../config/constants'

import './manager-workflow-panel.scss'
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Divider,
  Typography,
} from '@mui/material'

import {
  FormatListBulleted,
} from '@vedmarket/ui-kit'

import ApplicationSectionAccordion from './application-section-accordion'

import ClientCheckItemBlocks from './client-check-item-blocks'

import ManagerOutcomeSection from './manager-outcome-section'
import ManagerPipelineStep from './manager-pipeline-step'
import ManagerStageNavigation from './manager-stage-navigation'
import ManagerProposalSection from './manager-proposal-section'


import {
  showManagerOutcome,
  showManagerPackageReview,
  showManagerPreparationTools,
  showManagerProposalTools,
} from '../detail-visibility'

import {
  buildManagerWorkflowSteps,
  clientAcceptedProposal,
  clientRefused,
  getManagerWorkflowStep,
  managerStepShowsBody,
} from '../manager-workflow'

import ManagerPreparationSection from './manager-preparation-section'
import ManagerExecutorSection from './manager-executor-section'
import ManagerContactsSection from './manager-contacts-section'

const ManagerWorkflowPanel = ({ panel }: ApplicationManagerPanelProps) => {
  const props = panel
  const { app, managerView, checkStatuses, runAction, proposalForm, onProposalFormChange, onSaveProposal, onPublishProposal, outcomes, outcome, outcomeNote, onOutcomeChange, onOutcomeNoteChange, onUpdateOutcome } = props
  const { t } = useTranslation()

  const workflowSteps = useMemo(
    () => buildManagerWorkflowSteps(app),
    [ app ],
  )

  const currentStepId = workflowSteps.find((step) => (
    step.state === MANAGER_WORKFLOW_STEP_STATE.CURRENT
  ))?.id ?? MANAGER_WORKFLOW_STEP_ID.OUTCOME
  const [ requestedStepId, setSelectedStepId ] = useState<ManagerWorkflowStepId>(currentStepId)
  const requestedStep = getManagerWorkflowStep({ steps: workflowSteps, id: requestedStepId })
  const canSelectRequestedStep = requestedStep && managerStepShowsBody(requestedStep.state)
  const selectedStepId = canSelectRequestedStep ? requestedStepId : currentStepId

  useEffect(() => {
    setSelectedStepId(currentStepId)
  }, [ app.public_id, currentStepId ])

  const workflowStep = (id: ManagerWorkflowStepView['id']) => (
    getManagerWorkflowStep({ steps: workflowSteps, id: id })
  )

  let clientDecisionWaitingHint: React.ReactNode
  const isAwaitingClientDecision = app.status === APPLICATION_STATUS.PROPOSAL_READY
    && !app.contact_exchange_requested_at
  const showClientDecisionHint = isAwaitingClientDecision && !app.proposal?.declined_at
  if (showClientDecisionHint) {
    clientDecisionWaitingHint = (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {t('applications__text__mgrStep_clientDecisionWaiting')}
      </Typography>
    )
  } else {
    clientDecisionWaitingHint = undefined
  }

  return (
    <Box className='application-manager-workflow-panel'>
      <ApplicationSectionAccordion
        title={t('applications__text__applicationBySteps')}
        icon={<FormatListBulleted width={32} height={32} />}
      >
        <Box className='application-manager-workflow-panel__layout' sx={{
          gap: 3,
        }}>
          <ManagerStageNavigation
            steps={workflowSteps}
            app={app}
            selectedStepId={selectedStepId}
            onSelectStep={setSelectedStepId}
          />
          <Divider
            orientation='vertical'
            flexItem
            sx={{
              display: 'none',
              margin: 0,
              '@container manager-workflow (min-width: 640px)': {
                display: 'block',
              },
            }}
          />
          <Box className='application-manager-workflow-panel__content'>
            <ManagerPipelineStep selectedStepId={selectedStepId} step={workflowStep(MANAGER_WORKFLOW_STEP_ID.PREPARATION)}>
              <ManagerPreparationSection
                app={panel.app}
                managerView={panel.managerView}
                checkStatuses={panel.checkStatuses}
                executorId={panel.executorId}
                onExecutorIdChange={panel.onExecutorIdChange}
                executorChangeReason={panel.executorChangeReason}
                onExecutorChangeReasonChange={panel.onExecutorChangeReasonChange}
                companyOptions={panel.companyOptions}
                runAction={panel.runAction}
                onUpdateCheckItem={panel.onUpdateCheckItem}
                onPostCheckItemComment={panel.onPostCheckItemComment}
                onUploadCheckItemDocument={panel.onUploadCheckItemDocument}
                onDeleteCheckItemDocument={panel.onDeleteCheckItemDocument}
                onCompletePreparation={panel.onCompletePreparation}
                onAssignExecutor={panel.onAssignExecutor}
              />
            </ManagerPipelineStep>

            <ManagerPipelineStep selectedStepId={selectedStepId} step={workflowStep(MANAGER_WORKFLOW_STEP_ID.EXECUTOR)}>
              <ManagerExecutorSection
                app={panel.app}
                managerView={panel.managerView}
                executorId={panel.executorId}
                onExecutorIdChange={panel.onExecutorIdChange}
                executorChangeReason={panel.executorChangeReason}
                onExecutorChangeReasonChange={panel.onExecutorChangeReasonChange}
                companyOptions={panel.companyOptions}
                runAction={panel.runAction}
                onAssignExecutor={panel.onAssignExecutor}
              />
            </ManagerPipelineStep>

            <ManagerPipelineStep
              selectedStepId={selectedStepId}
              step={workflowStep(MANAGER_WORKFLOW_STEP_ID.CLIENT_PACKAGE)}

              waitingHint={!app.executor_transfer_requested_at ? (
                <Typography variant='body2' sx={{ color: 'text.primary' }}>
                  {t('applications__text__mgrStep_clientPackageWaiting')}
                </Typography>
              ) : undefined}
            >
              {showManagerPackageReview({ app: app, managerView: managerView }) && !showManagerPreparationTools({ app: app, managerView: managerView }) && (
                <ClientCheckItemBlocks
                  app={app}
                  publicId={app.public_id}
                  checkStatuses={checkStatuses}
                  runAction={runAction}
                  onPostCheckItemComment={panel.onPostCheckItemComment}
                  onConfirmTnvedChanges={panel.onConfirmTnvedChanges}
                  isManager
                />
              )}
            </ManagerPipelineStep>

            <ManagerPipelineStep selectedStepId={selectedStepId} step={workflowStep(MANAGER_WORKFLOW_STEP_ID.PROPOSAL)}>
              {showManagerProposalTools({ app: app, managerView: managerView }) && (
                <ManagerProposalSection
                  app={app}
                  proposalForm={proposalForm}
                  onProposalFormChange={onProposalFormChange}
                  runAction={runAction}
                  onSaveProposal={onSaveProposal}
                  onPublishProposal={onPublishProposal}
                />
              )}
            </ManagerPipelineStep>

            <ManagerPipelineStep
              selectedStepId={selectedStepId}
              step={workflowStep(MANAGER_WORKFLOW_STEP_ID.CLIENT_DECISION)}

              waitingHint={clientDecisionWaitingHint}
            >
              {clientAcceptedProposal(app) && (
                <Typography variant='body2' sx={{ color: 'text.primary' }}>
                  {t('applications__text__mgrStep_clientDecisionAcceptedBody')}
                </Typography>
              )}
              {clientRefused(app) && (
                <Typography variant='body2' sx={{ color: 'text.primary' }}>
                  {t('applications__text__mgrStep_clientDecisionRefusedBody')}
                </Typography>
              )}
            </ManagerPipelineStep>

            <ManagerPipelineStep selectedStepId={selectedStepId} step={workflowStep(MANAGER_WORKFLOW_STEP_ID.CONTACTS)}>
              <ManagerContactsSection
                app={panel.app}
                managerView={panel.managerView}
                contactForm={panel.contactForm}
                onContactFormChange={panel.onContactFormChange}
                clientContactForm={panel.clientContactForm}
                onClientContactFormChange={panel.onClientContactFormChange}
                onSaveClientContacts={panel.onSaveClientContacts}
                onSaveExecutorContacts={panel.onSaveExecutorContacts}
                onRevealContacts={panel.onRevealContacts}
              />
            </ManagerPipelineStep>

            <ManagerPipelineStep selectedStepId={selectedStepId} step={workflowStep(MANAGER_WORKFLOW_STEP_ID.OUTCOME)}>
              {showManagerOutcome({ app: app, managerView: managerView }) && (
                <ManagerOutcomeSection
                  app={app}
                  outcomes={outcomes}
                  outcome={outcome}
                  outcomeNote={outcomeNote}
                  onOutcomeChange={onOutcomeChange}
                  onOutcomeNoteChange={onOutcomeNoteChange}
                  runAction={runAction}
                  onUpdateOutcome={onUpdateOutcome}
                />
              )}
            </ManagerPipelineStep>
          </Box>
        </Box>
      </ApplicationSectionAccordion>
    </Box>
  )
}
export default ManagerWorkflowPanel
